import React, { useEffect, useState } from 'react';
import { Row, Stack, Text, PillButton, TextInput } from 'library/ui';
import { client, consoleLogInDev, verificationErrorMessage, GENERIC_VERIFICATION_SEND_ERROR } from 'shared';

export default function PhoneNumberVerification({
    phoneNumber,
    handleSuccess,
}: {
    phoneNumber: string;
    handleSuccess: (token: string) => void;
}) {
    const [code, setCode] = useState('');
    const [errorText, setErrorText] = useState('');
    const [display, setDisplay] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        // eslint-disable-next-line regexp/no-useless-quantifier
        const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            setDisplay(['(', match[2], ') ', match[3], '-', match[4]].join(''));
        }
        sendCode();
    }, []);

    function verifyCode() {
        setVerifying(true);
        setErrorText('');
        client(`check-verification-code/?code=${code}&receiver=${display.replace(/\D/g, '')}`, { method: 'POST' })
            .then((resp) => {
                if (resp.token) localStorage.setItem('token', resp.token);
                handleSuccess(resp.token);
            })
            .catch((err) => {
                consoleLogInDev(err);
                setErrorText(verificationErrorMessage(err));
                setCode('');
            })
            .finally(() => setVerifying(false));
    }

    function sendCode() {
        setSending(true);
        setErrorText('');
        client(`send-verification-text/?receiver=${phoneNumber}`, {
            method: 'POST',
        })
            .catch((err) => {
                consoleLogInDev(err);
                setErrorText(verificationErrorMessage(err, GENERIC_VERIFICATION_SEND_ERROR));
                setCode('');
            })
            .finally(() => setSending(false));
    }

    function onFormSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (code.length !== 4 || verifying) return;
        verifyCode();
    }

    return (
        <form onSubmit={onFormSubmit}>
            <Stack gap="lg">
                <Text size="sm">
                    Please enter the verification code sent to {display} to finish setting up your Tandem account.
                </Text>
                <TextInput
                    id="code"
                    value={code}
                    onChange={(value) => setCode(value.slice(0, 4))}
                    error={errorText}
                    fullWidth
                />
                <Row gap="sm" wrap>
                    <PillButton
                        variant="secondary"
                        disabled={sending}
                        id="sign-up-resend-code-button"
                        onClick={() => sendCode()}
                    >
                        {sending ? 'Sending...' : 'Resend Code'}
                    </PillButton>
                    <PillButton id="signup-finish-button" type="submit" disabled={code.length !== 4 || verifying}>
                        {verifying ? 'Verifying...' : 'Finish'}
                    </PillButton>
                </Row>
            </Stack>
        </form>
    );
}
